import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import { isBackofficeRole } from '../../../lib/roles';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { Archive, Search, Loader2, Database, AlertCircle, Eye } from 'lucide-react';

const fmtRM = (v) => `RM ${Number(v || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_COLORS = {
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  rejected: 'bg-rose-100 text-rose-800 border-rose-200',
  '': 'bg-slate-100 text-slate-600 border-slate-200',
};

const StatusPill = ({ value }) => {
  const cls = STATUS_COLORS[(value || '').toLowerCase()] || STATUS_COLORS[''];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {value || '—'}
    </span>
  );
};

export default function LegacyCommissions() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [year, setYear] = useState('all');
  const [bossStatus, setBossStatus] = useState('all');
  const [q, setQ] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const isAuthorised = !!user && isBackofficeRole(user.role);

  useEffect(() => {
    if (!isAuthorised) return;
    api.get('/legacy-commissions/summary')
      .then(res => setSummary(res.data))
      .catch(() => setSummary({ available: false }));
  }, [isAuthorised]);

  useEffect(() => {
    if (!isAuthorised) return;
    let cancel = false;
    setLoading(true);
    const params = { page, limit };
    if (q) params.q = q;
    if (year && year !== 'all') params.year = year;
    if (bossStatus && bossStatus !== 'all') params.boss_status = bossStatus;
    api.get('/legacy-commissions', { params })
      .then(res => {
        if (cancel) return;
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      })
      .catch(err => {
        if (cancel) return;
        toast.error(err.response?.data?.detail || 'Failed to load legacy commissions');
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [isAuthorised, page, limit, q, year, bossStatus]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const onSearch = (e) => { e.preventDefault(); setPage(1); setQ(searchInput.trim()); };

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/legacy-commissions/${id}`);
      setDetail(res.data);
      setDetailOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load detail');
    }
  };

  if (!isAuthorised) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="legacy-commissions-forbidden">
        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-rose-500" />
            <span>Admin / Account / PIC role required.</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (summary && summary.available === false) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="legacy-commissions-empty">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" /> Legacy Commissions Archive
            </CardTitle>
            <CardDescription>{summary.note || 'No legacy data imported yet.'}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="legacy-commissions-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Archive className="w-7 h-7 text-indigo-600" /> Legacy Commissions Archive
          </h1>
          <p className="text-slate-500 mt-1 max-w-2xl">
            Read-only browser of historical commission claims migrated from the legacy SAM Property system.
            <span className="font-medium text-slate-700"> Wallet credits are not synthesised from this data.</span>
          </p>
        </div>
        {summary && (
          <Badge variant="outline" className="text-xs px-3 py-1.5">
            {summary.total_claims?.toLocaleString()} claims · {summary.note || ''}
          </Badge>
        )}
      </div>

      {/* Headline cards */}
      {summary && summary.available !== false && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="legacy-commissions-headline">
          <Card><CardContent className="pt-6">
            <div className="text-sm text-slate-500">Total Legacy Claims</div>
            <div className="text-3xl font-semibold mt-1">{summary.total_claims?.toLocaleString()}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="text-sm text-slate-500">Σ Commission</div>
            <div className="text-3xl font-semibold mt-1 text-emerald-700">{fmtRM(summary.totals?.total_commission)}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="text-sm text-slate-500">Σ SPA Value</div>
            <div className="text-3xl font-semibold mt-1 text-indigo-700">{fmtRM(summary.totals?.total_spa_price)}</div>
          </CardContent></Card>
        </div>
      )}

      {/* Yearly chart-like summary */}
      {summary?.yearly?.length ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By Booking Year</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="legacy-yearly-summary">
              {summary.yearly.slice(0, 6).map((y) => (
                <div key={y.year} className="border rounded-lg p-3 hover:bg-slate-50 cursor-pointer transition" onClick={() => { setYear(y.year); setPage(1); }}>
                  <div className="text-xs text-slate-500">{y.year}</div>
                  <div className="text-xl font-semibold">{y.claims.toLocaleString()}</div>
                  <div className="text-xs text-emerald-700 mt-1">{fmtRM(y.commission)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={onSearch} className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[260px]">
              <label className="text-xs text-slate-500 mb-1 block">Search</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Buyer name, NRIC, unit, project, bank, agent name/code…"
                  className="pl-9"
                  data-testid="legacy-search-input"
                />
              </div>
            </div>
            <div className="w-32">
              <label className="text-xs text-slate-500 mb-1 block">Year</label>
              <Select value={year} onValueChange={(v) => { setYear(v); setPage(1); }}>
                <SelectTrigger data-testid="legacy-year-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All years</SelectItem>
                  {(summary?.yearly || []).map(y => (
                    <SelectItem key={y.year} value={y.year}>{y.year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <label className="text-xs text-slate-500 mb-1 block">Boss Status</label>
              <Select value={bossStatus} onValueChange={(v) => { setBossStatus(v); setPage(1); }}>
                <SelectTrigger data-testid="legacy-boss-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="h-10" data-testid="legacy-search-button">
              <Search className="w-4 h-4 mr-2" /> Search
            </Button>
            {(q || year !== 'all' || bossStatus !== 'all') && (
              <Button type="button" variant="outline" onClick={() => { setSearchInput(''); setQ(''); setYear('all'); setBossStatus('all'); setPage(1); }}>
                Clear
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Results · {total.toLocaleString()} matching</CardTitle>
          <div className="text-xs text-slate-500">Page {page} of {totalPages}</div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-slate-500">No matching legacy claims.</div>
          ) : (
            <div className="overflow-x-auto" data-testid="legacy-results-table">
              <table className="w-full text-sm">
                <thead className="border-b text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left py-2 pr-3">Booking Date</th>
                    <th className="text-left py-2 pr-3">Buyer</th>
                    <th className="text-left py-2 pr-3">NRIC</th>
                    <th className="text-left py-2 pr-3">Project / Unit</th>
                    <th className="text-right py-2 pr-3">SPA Price</th>
                    <th className="text-right py-2 pr-3">Commission</th>
                    <th className="text-left py-2 pr-3">Stages</th>
                    <th className="text-left py-2 pr-3">NEG (legacy)</th>
                    <th className="text-right py-2">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`legacy-row-${r.legacy_id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{r.booking_date || '—'}</td>
                      <td className="py-2 pr-3">{r.customer_full_name || '—'}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{r.customer_nric || '—'}</td>
                      <td className="py-2 pr-3"><span className="font-medium">{r.project_name || `#${r.project_legacy_id}`}</span> · <span className="text-slate-500">{r.unit_label || '—'}</span></td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">{fmtRM(r.spa_price)}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap text-emerald-700 font-medium">{fmtRM(r.commission)}</td>
                      <td className="py-2 pr-3">
                        <div className="flex gap-1 flex-wrap">
                          <StatusPill value={r.pic_status} />
                          <StatusPill value={r.admin_status} />
                          <StatusPill value={r.account_status} />
                          <StatusPill value={r.boss_status} />
                        </div>
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">{r.neg_legacy_id || '—'}</td>
                      <td className="py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openDetail(r.id)} data-testid={`legacy-view-${r.legacy_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-end gap-2" data-testid="legacy-pagination">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || loading}>Prev</Button>
              <span className="px-3 py-1.5 text-sm text-slate-600">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages || loading}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Legacy Claim · {detail?.claim_ref || detail?.id}</DialogTitle>
            <DialogDescription>Read-only view of source legacy_commission_claims document.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs text-slate-500">Booking Date</div><div>{detail.booking_date || '—'}</div></div>
                <div><div className="text-xs text-slate-500">Signing Date</div><div>{detail.signing_date || '—'}</div></div>
                <div><div className="text-xs text-slate-500">Customer</div><div>{detail.customer_full_name}</div></div>
                <div><div className="text-xs text-slate-500">NRIC</div><div className="font-mono">{detail.customer_nric}</div></div>
                <div><div className="text-xs text-slate-500">Project / Unit</div><div>{detail.project_name || `Project #${detail.project_legacy_id}`} · <span className="text-slate-500">{detail.unit_label}</span></div></div>
                <div><div className="text-xs text-slate-500">Bank / Loan</div><div>{detail.bank || '—'} · {fmtRM(detail.loan_amount)}</div></div>
                <div><div className="text-xs text-slate-500">SPA Price</div><div className="font-medium">{fmtRM(detail.spa_price)}</div></div>
                <div><div className="text-xs text-slate-500">Commission</div><div className="font-medium text-emerald-700">{fmtRM(detail.commission)}</div></div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div><div className="text-xs text-slate-500">PIC</div><StatusPill value={detail.pic_status} /></div>
                <div><div className="text-xs text-slate-500">Admin</div><StatusPill value={detail.admin_status} /></div>
                <div><div className="text-xs text-slate-500">Account</div><StatusPill value={detail.account_status} /></div>
                <div><div className="text-xs text-slate-500">Boss</div><StatusPill value={detail.boss_status} /></div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Agent Tree</div>
                <pre className="bg-slate-50 border rounded p-3 text-xs overflow-x-auto">{JSON.stringify(detail.agent_tree, null, 2)}</pre>
              </div>
              {detail.remark && (
                <div><div className="text-xs text-slate-500">Remark</div><div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm">{detail.remark}</div></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
